import { notification } from "antd";
import classNames from "classnames";
import cuid from "cuid";
import { useMemo, useState } from "react";
import { BiChevronLeft } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "shared/components/Header";
import {
  PayloadPathsProvider,
  toPayloadPathOptions,
} from "shared/components/PayloadPaths";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import { NAME_SPACES } from "shared/locales/constants";
import { CREATE_STEPS, STEPS } from "./constants";
import StepAnchors from "./StepAnchors";
import StepDetails from "./StepDetails";
import StepUpload from "./StepUpload";

const Create = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const CREATE = t("CREATE", { returnObjects: true });
  const STEP_LABELS = t("STEPS", { returnObjects: true });
  const navigate = useNavigate();
  const id = useMemo(() => cuid(), []);

  const [currentStep, setCurrentStep] = useState(0);
  const [file, setFile] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [mappings, setMappings] = useState({});
  const [name, setName] = useState("");

  const stepName = STEPS[currentStep];

  const [loadPayloadSchema, { data: schemaData, loading: schemaLoading }] =
    useLazyQuery(graphql.queries.DOCUMENT_PAYLOAD_SCHEMA);
  const payloadPaths = useMemo(
    () => toPayloadPathOptions(schemaData?.documentPayloadSchema?.paths),
    [schemaData],
  );
  const schemaPayload = schemaData?.documentPayloadSchema?.payload || null;

  const [addDocumentTemplate, { loading: saving }] = useMutation(
    graphql.mutations.CREATE_DOCUMENT_TEMPLATE,
    {
      refetchQueries: [
        {
          query: graphql.queries.DOCUMENT_TEMPLATES,
          awaitRefetchQueries: true,
        },
      ],
      onCompleted: () =>
        navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path),
      onError: () =>
        notification.error({
          message: CREATE.ERROR,
        }),
    },
  );

  const handleUploadDone = ({ file: uploadedFile, anchors: detected }) => {
    setFile(uploadedFile);
    setAnchors(detected);
    setMappings((prev) => {
      const next = {};
      for (const anchor of detected) {
        if (prev[anchor] !== undefined) next[anchor] = prev[anchor];
      }
      return next;
    });
    setCurrentStep(STEPS.indexOf(CREATE_STEPS.ANCHORS));
  };

  const handleSave = () => {
    addDocumentTemplate({
      variables: {
        data: {
          id,
          name,
          anchorMappings: mappings,
          anchors,
          file: {
            content: file.content,
            name: file.name,
            contentType: file.contentType,
          },
        },
      },
    });
  };

  const goBack = () => {
    if (currentStep === 0) {
      navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path);
      return;
    }
    setCurrentStep((s) => s - 1);
  };

  return (
    <>
      <Header
        title={CREATE.HEADER.TITLE}
        actions={[
          {
            text: CREATE.HEADER.BACK,
            onClick: () =>
              navigate(SCREENS.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS.LIST.path),
            type: "gray--link",
            icon: <BiChevronLeft size={20} />,
          },
        ]}
      />

      <div className="pricers-import__stepper">
        {STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;
          return (
            <div key={step} className="pricers-import__step-item">
              <div className="pricers-import__step-row">
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone || isActive,
                    "pricers-import__step-connector--hidden": idx === 0,
                  })}
                />
                <span
                  className={classNames("pricers-import__step-number", {
                    "pricers-import__step-number--active": isActive,
                    "pricers-import__step-number--done": isDone,
                  })}
                >
                  {isDone ? "✓" : idx + 1}
                </span>
                <div
                  className={classNames("pricers-import__step-connector", {
                    "pricers-import__step-connector--done": isDone,
                    "pricers-import__step-connector--hidden":
                      idx === STEPS.length - 1,
                  })}
                />
              </div>
              <span
                className={classNames("pricers-import__step-label", {
                  "pricers-import__step-label--active": isActive,
                  "pricers-import__step-label--done": isDone,
                })}
              >
                {STEP_LABELS[step]}
              </span>
            </div>
          );
        })}
      </div>

      {stepName === CREATE_STEPS.UPLOAD && (
        <StepUpload onNext={handleUploadDone} onBack={goBack} />
      )}

      {stepName === CREATE_STEPS.ANCHORS && (
        <PayloadPathsProvider paths={payloadPaths}>
          <StepAnchors
            anchors={anchors}
            mappings={mappings}
            onMappingChange={setMappings}
            onNext={() => setCurrentStep(STEPS.indexOf(CREATE_STEPS.DETAILS))}
            onBack={goBack}
            file={file}
            schemaLoading={schemaLoading}
            schemaPayload={schemaPayload}
            templateName={name}
            onLoadPayloadSchema={(subscriptionId) =>
              loadPayloadSchema({
                variables: { where: { id: subscriptionId } },
              })
            }
          />
        </PayloadPathsProvider>
      )}

      {stepName === CREATE_STEPS.DETAILS && (
        <StepDetails
          name={name}
          onNameChange={setName}
          onBack={goBack}
          onSave={handleSave}
          loading={saving}
        />
      )}
    </>
  );
};

export default Create;
